import React, { useCallback, useEffect } from 'react'
import {
  ActivityIndicator,
  Image,
  LayoutChangeEvent,
  StyleSheet,
  Text,
  useWindowDimensions,
  View
} from 'react-native'
import Animated, {
  Extrapolation,
  interpolate,
  runOnJS,
  useAnimatedStyle,
  useDerivedValue
} from 'react-native-reanimated'
import { observer } from 'mobx-react-lite'
import { Box } from 'native-base'
import { useThrottledCallback } from 'use-debounce'

import { useLayoutScrollHandler } from '../../../../hooks/useLayoutScrollHandler'
import { mxcToHttp } from '../../helpers'
import { useMessages } from '../../queries'
import { useCommunitiesTheme } from '../../themes'
import IsUserJoinedWrapper from '../IsUserJoinedWrapper'
import MessageList from '../MessageList'
import { useRoomContext } from '../RoomProvider'

import RoomFilters from './RoomFilters'
import RoomJoinButton from './RoomJoinButton'
import RoomTags from './RoomTags'
import RoomTopButtons from './RoomTopButtons'

const RoomHeaderWrapper = ({ roomTab }: { roomTab?: string }) => {
  const room = useRoomContext()
  const { room_id, avatar_url, name, space_id: spaceId } = room

  const communitiesTheme = useCommunitiesTheme()

  // const [headerState, setHeaderState] = React.useState({
  //   memberInfoHeight: 0,
  //   topButtonsHeight: 0,
  //   contentHeight: 0,
  //   layoutHeight: 0,
  //   filter: 'all'
  // })
  const [memberInfoHeight, setMemberInfoHeight] = React.useState(0)
  const [topButtonsHeight, setTopButtonsHeight] = React.useState(0)
  const [contentHeight, setContentHeight] = React.useState(0)
  const [layoutHeight, setLayoutHeight] = React.useState(0)
  const [filter, setFilter] = React.useState('all')

  const { scrollHandler, lastContentOffset } = useLayoutScrollHandler()
  const windowSize = useWindowDimensions()

  useEffect(() => {
    if (roomTab && roomTab !== filter) setFilter(roomTab)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomTab])

  const scrollOffsetY = lastContentOffset

  const H_SCROLL_DISTANCE = memberInfoHeight

  const headerHeight = 60
  const headerBackdropAnimatedStyle = useAnimatedStyle(() => {
    const height = interpolate(
      scrollOffsetY.value,
      [0, headerHeight],
      [headerHeight, 0],
      Extrapolation.CLAMP
    )
    return {
      height
    }
  })

  const isCloseToBottom = useCallback(
    (offsetY: number) => {
      let isClose = false

      if (contentHeight > 0 && layoutHeight > 0) {
        const reachBufferOffset = 100
        isClose = reachBufferOffset >= contentHeight - layoutHeight - offsetY
      }

      return isClose
    },
    [contentHeight, layoutHeight]
  )

  const onContentSizeChange = (width: number, height: number) => {
    setContentHeight(Math.floor(height))
  }

  const onScrollLayout = (layout: LayoutChangeEvent) => {
    const height = layout.nativeEvent.layout.height
    setLayoutHeight(Math.floor(height))
  }

  const {
    data: queryMessages,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    isFetching
  } = useMessages(room_id, filter, true)

  const throttledFetch = useThrottledCallback(
    () => {
      if (isFetchingNextPage) return null
      fetchNextPage({
        // @ts-expect-error this is existing
        pageParam: queryMessages?.nextPage
      }).catch(error => {
        console.error('Error fetching next page', error)
      })
    },
    500,
    {
      leading: true,
      trailing: false
    }
  )

  const checkShouldFetchNextPage = useCallback(
    (offsetY: number, hasNextPage?: boolean, isFetchingNextPage?: boolean) => {
      if (offsetY > 0 && isCloseToBottom(offsetY)) {
        // @ts-expect-error this is existing
        if (!isFetchingNextPage && queryMessages?.nextPage) {
          throttledFetch()
        }
      }
    },
    // @ts-expect-error this is existing
    [isCloseToBottom, queryMessages?.nextPage, throttledFetch]
  )

  useDerivedValue(() => {
    runOnJS(checkShouldFetchNextPage)(scrollOffsetY.value, hasNextPage, isFetchingNextPage)
  }, [isCloseToBottom, hasNextPage, isFetchingNextPage, isFetching])

  const roomAvatarUrl = avatar_url ? mxcToHttp(avatar_url, 'matrix.api.vatominc.com') : avatar_url

  return (
    <View
      style={{
        backgroundColor: communitiesTheme.background,
        height: '100%'
      }}
    >
      <Animated.View
        // style={headerTopAnimatedStyle}
        onLayout={event => {
          const height = event.nativeEvent.layout.height
          setTopButtonsHeight(height)
        }}
      >
        <RoomTopButtons scrollDistance={H_SCROLL_DISTANCE} scrollOffsetY={scrollOffsetY} />
      </Animated.View>
      <Animated.View
        style={[
          {
            backgroundColor: communitiesTheme.header.background,
            borderBottomLeftRadius: 16,
            borderBottomRightRadius: 16,
            width: '100%',
            position: 'absolute',
            top: topButtonsHeight - 2
          },
          headerBackdropAnimatedStyle
        ]}
      />

      <Animated.ScrollView
        style={{
          // height: windowSize.height - topButtonsHeight - 72
          height: windowSize.height - topButtonsHeight
        }}
        scrollEventThrottle={32}
        onScroll={scrollHandler}
        // contentContainerStyle={{
        //   paddingBottom: getFooterHeight() + topButtonsHeight
        // }}
        onContentSizeChange={onContentSizeChange}
        onLayout={onScrollLayout}
        stickyHeaderIndices={[1]}
        removeClippedSubviews
        nestedScrollEnabled
      >
        <View
          key="room-info"
          onLayout={event => {
            const height = event.nativeEvent.layout.height
            setMemberInfoHeight(height)
          }}
        >
          <View
            style={{
              paddingHorizontal: 16,
              paddingBottom: 20
            }}
          >
            <Image
              accessibilityIgnoresInvertColors
              source={{ uri: roomAvatarUrl }}
              style={[styles.roomAvatar, { borderColor: communitiesTheme.roomAvatarBorder }]}
            />
            <Text style={[styles.roomName, { color: communitiesTheme.roomTitle }]}>{name}</Text>
            <RoomTags />
            <RoomJoinButton />
          </View>
        </View>

        <RoomFilters setFilter={setFilter} filter={filter} />

        <IsUserJoinedWrapper roomId={room_id}>
          <MessageList roomId={room_id} filter={filter} spaceId={spaceId} />
          {isFetchingNextPage && (
            <Box marginY={10}>
              <ActivityIndicator />
            </Box>
          )}
        </IsUserJoinedWrapper>
      </Animated.ScrollView>
    </View>
  )
}

const styles = StyleSheet.create({
  roomName: {
    fontSize: 20,
    marginTop: 15,
    alignSelf: 'center',
    fontWeight: 'bold'
  },
  roomAvatar: {
    height: 100,
    width: 100,
    borderRadius: 12,
    borderWidth: 6,
    marginTop: 10,
    alignSelf: 'center'
  }
})

export default observer(RoomHeaderWrapper)
